import Storage from '@/core/services/cookie-storage';
import { refreshToken } from '@/core/services/fetchers';
import { assert } from './assert';
import buildUrl from './build-url';
import ResponseError from './response-error';

type ApiConfig = {
  endpoint: string;
  headers?: Record<string, string>;
  credentials?: RequestCredentials;
};

type Api = {
  get: <GetResponse>(
    path: string,
    config?: ApiMethodConfig,
  ) => Promise<GetResponse>;
  put: <PutResponse>(
    path: string,
    data: Record<string, any>,
    config?: ApiMethodConfig,
  ) => Promise<PutResponse>;
  post: <PostResponse>(
    path: string,
    data: Record<string, any> | FormData,
    config?: ApiMethodConfig,
  ) => Promise<PostResponse>;
  patch: <PatchResponse>(
    path: string,
    data: Record<string, any>,
    config?: ApiMethodConfig,
  ) => Promise<PatchResponse>;
  delete: <DeleteResponse>(
    path: string,
    config?: ApiMethodConfig,
  ) => Promise<DeleteResponse>;
};

type ApiMethodConfig = RequestInit & {
  params?: Record<string, string>;
  signal?: AbortSignal;
  skipRefreshToken?: boolean;
  next?: {
    revalidate?: number | false;
    tags?: string[];
  };
};

const clearAuthSession = async () => {
  await Storage.remove('hasAuthSession');
  await Storage.remove('accessToken');
  await Storage.remove('refreshToken');
};

const createApi = ({
  endpoint,
  headers,
  credentials: defaultCredentials,
}: ApiConfig): Api => {
  assert(endpoint, 'endpoint is required');

  const refreshTokenAndRetry = async <T>(
    method: RequestInit['method'],
    path: string,
    config: ApiMethodConfig,
  ): Promise<T> => {
    try {
      await refreshToken();

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return await getResponse(method, path, {
        ...config,
        skipRefreshToken: true,
      });
    } catch (error) {
      await clearAuthSession();
      throw error;
    }
  };

  const getResponse = async <T>(
    method: RequestInit['method'],
    path: string,
    config: ApiMethodConfig,
  ): Promise<T> => {
    const { params = {}, skipRefreshToken = false, next: nextCache, ...init } =
      config;

    init.headers = new Headers({
      ...headers,
      ...(init.headers instanceof Headers
        ? Object.fromEntries(init.headers.entries())
        : init.headers),
    });
    init.method = method;

    if (init.credentials === undefined && defaultCredentials !== undefined) {
      init.credentials = defaultCredentials;
    }

    const url = endpoint + buildUrl(path, params);

    const response = await fetch(url, {
      ...init,
      ...(nextCache ? { next: nextCache } : {}),
    });

    if (!response.ok) {
      if (response.status === 401 && !skipRefreshToken) {
        return refreshTokenAndRetry(method, path, config);
      }

      throw new ResponseError(response);
    }

    if (response.status === 204) return {} as T;

    const data: T = await response.json();

    return data;
  };

  const api = {
    get: async <GetResponse>(
      path: string,
      config?: ApiMethodConfig,
    ): Promise<GetResponse> => getResponse('GET', path, config || {}),
    put: async <PutResponse>(
      path: string,
      data: Record<string, any>,
      config?: ApiMethodConfig,
    ): Promise<PutResponse> =>
      getResponse('PUT', path, {
        ...config,
        headers: {
          'Content-Type': 'application/json',
          ...config?.headers,
        },
        body: JSON.stringify(data),
      }),
    post: async <PostResponse>(
      path: string,
      data: Record<string, any> | FormData,
      config?: ApiMethodConfig,
    ): Promise<PostResponse> => {
      const isFormData = data instanceof FormData;
      return getResponse('POST', path, {
        ...config,
        headers: isFormData
          ? config?.headers
          : {
              'Content-Type': 'application/json',
              ...config?.headers,
            },
        body: isFormData ? data : JSON.stringify(data),
      });
    },
    patch: async <PatchResponse>(
      path: string,
      data: Record<string, any>,
      config?: ApiMethodConfig,
    ): Promise<PatchResponse> =>
      getResponse('PATCH', path, {
        ...config,
        headers: {
          'Content-Type': 'application/json',
          ...config?.headers,
        },
        body: JSON.stringify(data),
      }),
    delete: async <DeleteResponse>(
      path: string,
      config?: ApiMethodConfig,
    ): Promise<DeleteResponse> => getResponse('DELETE', path, config || {}),
  };
  return api;
};

export default createApi;
